import classNames from "classnames";
import React from "react";
import { useTranslation } from "react-i18next";

import style from "./side-panel-info.scss";
import { getTemplateTypeBadge } from "./workflow-templates/PublishedWorkflowTemplate";
import { DOT } from "domain/globalConstants";
import { ConnectedWorkflows, WorkflowEntityType, WorkflowTableData } from "domain/workflows";
import { TemplateTableData } from "domain/workflowTemplates";
import { Profile, profileToNameMap } from "services/workflows/WorkflowService";
import { useTheme } from "utils/useTheme";

interface SidePanelInfoProps {
    connectedWorkflows: ConnectedWorkflows;
    product?: string;
    description: string;
    version?: string;
    type: WorkflowEntityType;
    selectedRow?: TemplateTableData | WorkflowTableData;
    isSubWorkflow?: boolean;
}

export function SidePanelInfo(props: SidePanelInfoProps): JSX.Element | null {
    const { t } = useTranslation();
    const theme = useTheme();
    const templateRow = props.selectedRow as TemplateTableData;

    const translatedWorkflows: [string, string[]][] = [
        [t("WorkflowsTable.connectedWorkflows.parentWorkflows"), props.connectedWorkflows.parentWorkflows],
        [t("WorkflowsTable.connectedWorkflows.subWorkflows"), props.connectedWorkflows.subWorkflows],
        [t("WorkflowsTable.connectedWorkflows.runDiagnoseWorkflows"), props.connectedWorkflows.runDiagnoseWorkflows],
        [
            t("WorkflowsTable.connectedWorkflows.runProcessflowWorkflows"),
            props.connectedWorkflows.runProcessFlowWorkflows,
        ],
        [t("WorkflowsTable.connectedWorkflows.gradingPanelWorkflows"), props.connectedWorkflows.gradingPanelWorkflows],
        [t("WorkflowsTable.connectedWorkflows.collectWorkflows"), props.connectedWorkflows.collectWorkflows],
        [t("WorkflowsTable.connectedWorkflows.forEachWorkflows"), props.connectedWorkflows.forEachWorkflows],
        [
            t("WorkflowsTable.connectedWorkflows.advancedGradingWorkflows"),
            props.connectedWorkflows.advancedGradingWorkflows,
        ],
    ];
    const totalConnectedWorkflows = translatedWorkflows.reduce((sum, [, workflows]) => sum + workflows.length, 0);
    const descriptionLength: number = props.description ? props.description.trim().length : 0;

    if (props.type === WorkflowEntityType.TEMPLATE || props.type === WorkflowEntityType.SUB_WORKFLOW) {
        const hasCategories = !props.isSubWorkflow && templateRow?.useCases && templateRow.useCases.length > 0;
        const hasNoContent = descriptionLength === 0 && totalConnectedWorkflows === 0 && !hasCategories;

        return (
            <div className={classNames(style.container, style.templateContainer)}>
                <p>
                    {profileToNameMap.get(templateRow?.profile as Profile) || templateRow?.profile}{" "}
                    {templateRow?.editorVersion}
                </p>

                {!props.isSubWorkflow && (
                    <>
                        <div className={style.templateBadges}>
                            <div>
                                {templateRow?.templateType &&
                                    getTemplateTypeBadge(
                                        templateRow.templateType,
                                        t,
                                        theme.readyToUseSkyBlueColor,
                                        theme.modularPurpleColor,
                                        theme.integrationGreenColor
                                    )}
                            </div>
                        </div>

                        {hasCategories && (
                            <div className={style.templateCategories}>
                                {templateRow?.useCases.map((category) => (
                                    <div key={category} className={style.templateItem}>
                                        {category}
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}

                {hasNoContent ? (
                    <div className={style.emptyMessage}>{t("WorkflowsTable.sidePanel.noInfoContent")}</div>
                ) : (
                    <>
                        {descriptionLength > 0 && (
                            <>
                                <div>
                                    <b>{t("WorkflowsTable.description")}</b>
                                </div>
                                <p>{props.description}</p>
                            </>
                        )}

                        {totalConnectedWorkflows > 0 && (
                            <>
                                <b>{t("WorkflowsTable.connectedWorkflows.title")}</b>
                                {translatedWorkflows.map(
                                    ([key, workflowArray]) =>
                                        workflowArray.length > 0 && (
                                            <div key={key}>
                                                <small>{key}</small>
                                                {workflowArray.map((workflow, index) => (
                                                    <p key={index}>{workflow}</p>
                                                ))}
                                            </div>
                                        )
                                )}
                            </>
                        )}
                    </>
                )}
            </div>
        );
    } else {
        const hasNoContent = descriptionLength === 0 && totalConnectedWorkflows === 0;

        return (
            <div className={style.container}>
                {props.product && (
                    <div>
                        <small>
                            {props.product}
                            {" " + props.version}
                            {totalConnectedWorkflows > 0 &&
                                " " +
                                    DOT +
                                    ` ${totalConnectedWorkflows} ${t("WorkflowsTable.sidePanel.tabs.info.connectedWorkflows")}`}
                        </small>
                    </div>
                )}
                {hasNoContent ? (
                    <div className={style.emptyMessage}>{t("WorkflowsTable.sidePanel.noInfoContent")}</div>
                ) : (
                    <>
                        <div>{descriptionLength > 0 && <b>{t("WorkflowsTable.description")}</b>}</div>
                        <p>{descriptionLength > 0 ? props.description : ""}</p>
                        {totalConnectedWorkflows > 0 && (
                            <>
                                <b>{t("WorkflowsTable.connectedWorkflows.title")}</b>
                                {translatedWorkflows.map(
                                    ([key, workflowArray]) =>
                                        workflowArray.length > 0 && (
                                            <div key={key}>
                                                <small>{key}</small>
                                                {workflowArray.map((workflow, index) => (
                                                    <p key={index}>{workflow}</p>
                                                ))}
                                            </div>
                                        )
                                )}
                            </>
                        )}
                    </>
                )}
            </div>
        );
    }
}
